import {
  CategoryLessonShelf,
  LessonsCatalogEmptyState,
  LessonsCatalogFilters,
  LessonsCatalogHero,
} from "@app/grades/components/LessonsCatalog";
import { useLessonsCatalogPage } from "@app/grades/hooks/use-lessons-catalog-page.hook";
import { type Lesson } from "@core/grades/types";
import { type BreadcrumbItem, type PaginationInfo } from "@core/shared/types";
import { URLS } from "@core/shared/utils";
import PaginationSection from "@design-system/components/Pagination/Pagination";
import { trans } from "@mongez/localization";

type LessonsListPageContentProps = {
  lessons: Lesson[];
  paginationInfo: PaginationInfo;
  pageTitle?: React.ReactNode;
  pageDescription?: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
};

export function LessonsListPageContent({
  lessons,
  paginationInfo,
  pageTitle,
  pageDescription,
  breadcrumbs,
}: LessonsListPageContentProps) {
  const catalog = useLessonsCatalogPage(lessons);
  const pageBreadcrumbs = breadcrumbs || [
    {
      text: trans("grades.lessons"),
      url: URLS.lessons.list,
    },
  ];

  return (
    <div className="app-page-canvas min-h-screen bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.08),_transparent_34rem)] pb-12 dark:bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.12),_transparent_34rem)]">
      <div className="container mx-auto space-y-7 px-4 py-5 sm:px-6 sm:py-7 lg:space-y-9 lg:px-8">
        <LessonsCatalogHero
          breadcrumbs={pageBreadcrumbs}
          categoriesCount={catalog.categories.length}
          description={pageDescription || trans("grades.lessonsDescription")}
          title={pageTitle || trans("grades.lessons")}
          totalLessons={paginationInfo.total}
          visibleLessons={lessons.length}
        />

        <LessonsCatalogFilters
          activeFilters={catalog.activeFilters}
          categoryOptions={catalog.categories.map((category) => ({
            label: category.name,
            value: category.id.toString(),
          }))}
          gradeOptions={catalog.grades.map((grade) => ({
            label: grade.name,
            value: grade.id.toString(),
          }))}
          hideGradeFilter={catalog.isLoggedIn}
          onCategoryChange={catalog.changeCategory}
          onClearFilters={catalog.clearFilters}
          onClearSearch={catalog.clearSearch}
          onGradeChange={catalog.changeGrade}
          onRemoveFilter={catalog.removeFilter}
          onSearchChange={catalog.setSearchTerm}
          onSearchSubmit={catalog.submitSearch}
          searchTerm={catalog.searchTerm}
          selectedCategory={catalog.selectedCategory}
          selectedGrade={catalog.selectedGrade}
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-primary-600 dark:text-primary-300">
              {trans("lessonCatalog.learningLibrary")}
            </p>
            <h2 className="mt-1 text-xl font-black text-slate-950 dark:text-white sm:text-2xl">
              {trans("lessonCatalog.resultsTitle")}
            </h2>
          </div>
          <span className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 sm:text-sm">
            {trans("grades.showingResults", {
              count: lessons.length,
              total: paginationInfo.total,
            })}
          </span>
        </div>

        {lessons.length > 0 ? (
          <>
            <div className="space-y-12">
              {catalog.lessonsByCategory.map((group, index) => (
                <CategoryLessonShelf
                  category={group.category}
                  categoryName={group.name}
                  key={group.name}
                  lessons={group.lessons}
                  shelfIndex={index}
                />
              ))}
            </div>

            {paginationInfo.pages > 1 ? (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-2 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <PaginationSection paginationInfo={paginationInfo} />
              </div>
            ) : null}
          </>
        ) : (
          <LessonsCatalogEmptyState
            hasFilters={catalog.activeFilters.length > 0}
            onClearFilters={catalog.clearFilters}
          />
        )}
      </div>
    </div>
  );
}
